import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTask, updateTask, addComment, uploadScreenshot, uploadAttachment, downloadFileBlob, getTeams, getBrands, getUsers, getTaskABTests } from '@/lib/api';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, PaperPlaneTilt, Paperclip, Clock, User, ArrowsLeftRight, ChatText,
  Image, LinkSimple, CalendarBlank, Warning, Lightning, Flask, ArrowUp, ArrowDown, Minus
} from '@phosphor-icons/react';

const STATUSES = ['New Idea Repository', 'Backlog', 'In Discussion', 'In Design', 'In Development', 'In QA', 'Blocked', 'Completed', 'Implemented'];
const STATUS_COLORS = {
  'New Idea Repository': '#C4B5FD', 'Backlog': '#E8E8E8', 'In Discussion': '#FFE7B3', 'In Design': '#D8B4E2',
  'In Development': '#B3D4FF', 'In QA': '#FFC2B3', 'Blocked': '#FF3B30',
  'Completed': '#34C759', 'Implemented': '#10B981',
};
const PRIORITY_COLORS = { High: '#FF3B30', Medium: '#FFB800', Low: '#34C759' };

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentScreenUrl, setCurrentScreenUrl] = useState(null);
  const [proposedScreenUrl, setProposedScreenUrl] = useState(null);
  const [tab, setTab] = useState('details');
  const [abTests, setAbTests] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [taskRes, teamsRes, brandsRes, usersRes, abRes] = await Promise.all([
        getTask(id), getTeams(), getBrands(), getUsers(), getTaskABTests(id)
      ]);
      setTask(taskRes.data);
      setTeams(teamsRes.data);
      setBrands(brandsRes.data);
      setUsers(usersRes.data);
      setAbTests(abRes.data);

      if (taskRes.data.current_screenshot) {
        try {
          const url = await downloadFileBlob(taskRes.data.current_screenshot);
          setCurrentScreenUrl(url);
        } catch (err) { console.error('Screenshot load error:', err.message); }
      }
      if (taskRes.data.proposed_screenshot) {
        try {
          const url = await downloadFileBlob(taskRes.data.proposed_screenshot);
          setProposedScreenUrl(url);
        } catch (err) { console.error('Screenshot load error:', err.message); }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await updateTask(id, { status: newStatus });
      setTask(prev => ({ ...prev, ...res.data }));
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleFieldUpdate = async (field, value) => {
    try {
      const res = await updateTask(id, { [field]: value });
      setTask(prev => ({ ...prev, ...res.data }));
      if (field === 'assigned_to') loadData();
    } catch (e) { console.error(e); }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSubmittingComment(true);
    try {
      await addComment(id, comment.trim());
      setComment('');
      loadData();
    } catch (e) { console.error(e); }
    setSubmittingComment(false);
  };

  const handleScreenshot = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await uploadScreenshot(id, type, file);
        loadData();
      } catch (err) { console.error(err); }
    };
    input.click();
  };

  const handleAttachment = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await uploadAttachment(id, file);
        loadData();
      } catch (err) { console.error(err); }
    };
    input.click();
  };

  if (loading) return <div className="p-8 text-[#555555]" data-testid="task-detail-loading">Loading task...</div>;
  if (!task) return <div className="p-8 text-[#555555]">Task not found</div>;

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t.name]));
  const brandMap = Object.fromEntries(brands.map(b => [b.id, b.name]));
  const isOverdue = task.deadline && !['Completed', 'Implemented'].includes(task.status) && new Date(task.deadline) < new Date();

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="task-detail-page">
      {/* Back button */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#555555] hover:text-[#0F0F0F] mb-5 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} />
        Back
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: STATUS_COLORS[task.status] + '40', color: task.status === 'Blocked' ? '#FF3B30' : '#1A1A1A' }}>
              {task.status}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: PRIORITY_COLORS[task.priority] + '18', color: PRIORITY_COLORS[task.priority] }}>
              {task.priority}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8E8E8] text-[#1A1A1A] font-medium">{task.task_type}</span>
            {isOverdue && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-[#FF3B30] font-medium flex items-center gap-1">
                <Warning size={10} /> Overdue
              </span>
            )}
          </div>
          <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F]" data-testid="task-title">{task.title}</h1>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex gap-0 border-b border-[#E8E8E8]">
            {['details', 'designs', 'ab-tests', 'comments', 'history'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-[#32B4AC] text-[#0F0F0F]' : 'border-transparent text-[#555555] hover:text-[#0F0F0F]'}`}
                data-testid={`tab-${t}`}
              >
                {t === 'ab-tests' ? (
                  <span className="flex items-center gap-1.5">
                    <Flask size={13} weight="fill" />
                    A/B Tests
                    {abTests.length > 0 && <span className="text-[10px] bg-[#32B4AC] text-white px-1.5 py-0 rounded-full">{abTests.length}</span>}
                  </span>
                ) : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Details tab */}
          {tab === 'details' && (
            <div className="space-y-5">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-2">Description</h4>
                <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap" data-testid="task-description">
                  {task.description || 'No description provided'}
                </p>
              </div>

              {/* Links */}
              {(task.figma_link || task.gdocs_link) && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-2">Links</h4>
                  <div className="space-y-1">
                    {task.figma_link && (
                      <a href={task.figma_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#32B4AC] hover:underline" data-testid="figma-link">
                        <LinkSimple size={14} /> Figma Design
                      </a>
                    )}
                    {task.gdocs_link && (
                      <a href={task.gdocs_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#32B4AC] hover:underline" data-testid="gdocs-link">
                        <LinkSimple size={14} /> Google Docs
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Attachments</h4>
                  <button onClick={handleAttachment} className="text-xs text-[#32B4AC] hover:underline flex items-center gap-1" data-testid="add-attachment-btn">
                    <Paperclip size={12} /> Add
                  </button>
                </div>
                {(task.attachments || []).length > 0 ? (
                  <div className="space-y-1">
                    {task.attachments.map(a => (
                      <div key={a.id} className="flex items-center gap-2 py-1.5 px-2 bg-[#F4F4F4] rounded-md text-xs text-[#1A1A1A]">
                        <Paperclip size={12} className="text-[#555555]" />
                        <span className="truncate">{a.original_filename}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-[#555555]">No attachments</p>}
              </div>
            </div>
          )}

          {/* Designs tab */}
          {tab === 'designs' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-[#E8E8E8] rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Current Design</h4>
                    <button onClick={() => handleScreenshot('current')} className="text-xs text-[#32B4AC] hover:underline flex items-center gap-1" data-testid="upload-current-screenshot-btn">
                      <Image size={12} /> Upload
                    </button>
                  </div>
                  {currentScreenUrl ? (
                    <img src={currentScreenUrl} alt="Current design" className="w-full rounded-md border border-[#E8E8E8]" data-testid="current-screenshot" />
                  ) : <div className="h-40 bg-[#F4F4F4] rounded-md flex items-center justify-center text-xs text-[#555555]">No screenshot uploaded</div>}
                </div>
                <div className="border border-[#E8E8E8] rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Proposed Design</h4>
                    <button onClick={() => handleScreenshot('proposed')} className="text-xs text-[#32B4AC] hover:underline flex items-center gap-1" data-testid="upload-proposed-screenshot-btn">
                      <Image size={12} /> Upload
                    </button>
                  </div>
                  {proposedScreenUrl ? (
                    <img src={proposedScreenUrl} alt="Proposed design" className="w-full rounded-md border border-[#E8E8E8]" data-testid="proposed-screenshot" />
                  ) : <div className="h-40 bg-[#F4F4F4] rounded-md flex items-center justify-center text-xs text-[#555555]">No screenshot uploaded</div>}
                </div>
              </div>
            </div>
          )}

          {/* A/B Tests tab */}
          {tab === 'ab-tests' && (
            <div className="space-y-4" data-testid="task-ab-tests-section">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555]">Linked A/B Tests</h4>
                <button
                  onClick={() => navigate(`/ab-tests/new?task=${id}`)}
                  className="text-xs px-3 py-1.5 bg-[#32B4AC] text-white rounded-md hover:bg-[#2A9B94] font-medium flex items-center gap-1"
                  data-testid="create-ab-test-from-task-btn"
                >
                  <Flask size={12} weight="fill" /> New A/B Test
                </button>
              </div>
              {abTests.length > 0 ? (
                <div className="space-y-2">
                  {abTests.map(t => {
                    const AB_STATUS_COLORS = { 'Not Started': '#E8E8E8', 'Running': '#B3D4FF', 'Completed': '#34C759', 'Implemented': '#10B981' };
                    return (
                      <button
                        key={t.id}
                        onClick={() => navigate(`/ab-tests/${t.id}`)}
                        className="w-full border border-[#E8E8E8] rounded-md p-4 hover:border-[#1A1A1A] transition-colors text-left group"
                        data-testid={`task-ab-test-${t.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Flask size={14} weight="fill" className="text-[#32B4AC]" />
                            <span className="text-sm font-medium text-[#0F0F0F]">{t.test_name}</span>
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (AB_STATUS_COLORS[t.status] || '#E8E8E8') + '60' }}>
                            {t.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#555555]">
                          <span>{t.primary_metric}</span>
                          {t.percent_change != null && (
                            <span className={`font-semibold flex items-center gap-0.5 ${t.percent_change > 0 ? 'text-[#34C759]' : t.percent_change < 0 ? 'text-[#FF3B30]' : 'text-[#555555]'}`}>
                              {t.percent_change > 0 ? <ArrowUp size={10} /> : t.percent_change < 0 ? <ArrowDown size={10} /> : <Minus size={10} />}
                              {t.percent_change > 0 ? '+' : ''}{t.percent_change}%
                            </span>
                          )}
                          {t.winner && (
                            <span className={`font-bold ${t.winner === 'B' ? 'text-[#32B4AC]' : t.winner === 'A' ? 'text-[#FF3B30]' : 'text-[#FFB800]'}`}>
                              Winner: {t.winner === 'Inconclusive' ? 'Inconclusive' : `Variant ${t.winner}`}
                            </span>
                          )}
                          {t.duration_days != null && <span>{t.duration_days}d</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 border border-[#E8E8E8] rounded-md">
                  <Flask size={28} weight="fill" className="text-[#E8E8E8] mx-auto mb-2" />
                  <p className="text-sm text-[#555555]">No A/B tests linked to this task</p>
                  <p className="text-xs text-[#555555] mt-1">Create one to start tracking experiments</p>
                </div>
              )}
            </div>
          )}

          {/* Comments tab */}
          {tab === 'comments' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  data-testid="comment-input"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="min-h-[60px] text-sm border-[#E8E8E8] rounded-md resize-none"
                />
                <button
                  data-testid="submit-comment-btn"
                  onClick={handleAddComment}
                  disabled={submittingComment || !comment.trim()}
                  className="h-10 px-3 bg-[#32B4AC] text-white rounded-md hover:bg-[#2A9B94] disabled:opacity-50 shrink-0 self-end"
                >
                  <PaperPlaneTilt size={16} />
                </button>
              </div>
              <div className="space-y-3">
                {(task.comments || []).map(c => (
                  <div key={c.id} className="border border-[#E8E8E8] rounded-md p-3" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-[#0F0F0F]">{c.author_name}</span>
                      <span className="text-[10px] text-[#555555]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-[#1A1A1A]">{c.text}</p>
                  </div>
                ))}
                {(task.comments || []).length === 0 && <p className="text-xs text-[#555555]">No comments yet</p>}
              </div>
            </div>
          )}

          {/* History tab */}
          {tab === 'history' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Status History</h4>
                <div className="space-y-2">
                  {(task.status_logs || []).map(log => (
                    <div key={log.id} className="flex items-center gap-3 text-xs py-2 border-b border-[#F4F4F4]" data-testid={`status-log-${log.id}`}>
                      <Clock size={12} className="text-[#555555] shrink-0" />
                      <span className="text-[#555555]">{new Date(log.timestamp).toLocaleString()}</span>
                      {log.from_status && <span className="px-1.5 py-0.5 rounded-full bg-[#F4F4F4]">{log.from_status}</span>}
                      <ArrowsLeftRight size={10} className="text-[#555555]" />
                      <span className="px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: (STATUS_COLORS[log.to_status] || '#E8E8E8') + '40' }}>{log.to_status}</span>
                      <span className="text-[#555555]">by {log.changed_by_name}</span>
                    </div>
                  ))}
                  {(task.status_logs || []).length === 0 && <p className="text-xs text-[#555555]">No history</p>}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Ownership History</h4>
                <div className="space-y-2">
                  {(task.ownership_logs || []).map(log => (
                    <div key={log.id} className="flex items-center gap-3 text-xs py-2 border-b border-[#F4F4F4]" data-testid={`ownership-log-${log.id}`}>
                      <User size={12} className="text-[#555555] shrink-0" />
                      <span className="text-[#555555]">{new Date(log.timestamp).toLocaleString()}</span>
                      <span className="text-[#1A1A1A]">{log.from_owner_name || 'None'}</span>
                      <ArrowsLeftRight size={10} className="text-[#555555]" />
                      <span className="font-medium text-[#0F0F0F]">{log.to_owner_name || 'Unknown'}</span>
                    </div>
                  ))}
                  {(task.ownership_logs || []).length === 0 && <p className="text-xs text-[#555555]">No ownership changes</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* Status */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Status</h4>
            <select
              data-testid="task-status-select"
              value={task.status}
              onChange={e => handleStatusChange(e.target.value)}
              className="w-full h-9 text-sm border border-[#E8E8E8] rounded-md px-2 bg-white focus:ring-1 focus:ring-[#32B4AC]"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Assignment */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Assignment</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#555555] block mb-1">Assigned To</label>
                <select
                  data-testid="task-assigned-to-select"
                  value={task.assigned_to || ''}
                  onChange={e => handleFieldUpdate('assigned_to', e.target.value || null)}
                  className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white"
                >
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#555555] block mb-1">Team</label>
                <select
                  data-testid="task-team-select"
                  value={task.assigned_team || ''}
                  onChange={e => handleFieldUpdate('assigned_team', e.target.value || null)}
                  className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white"
                >
                  <option value="">No team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Details</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#555555] block mb-1">Priority</label>
                <select
                  data-testid="task-priority-select"
                  value={task.priority}
                  onChange={e => handleFieldUpdate('priority', e.target.value)}
                  className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#555555] block mb-1">Brand</label>
                <select
                  data-testid="task-brand-select"
                  value={task.brand_id || ''}
                  onChange={e => handleFieldUpdate('brand_id', e.target.value || null)}
                  className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white"
                >
                  <option value="">No brand</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#555555] block mb-1">Domain</label>
                <select
                  data-testid="task-domain-select"
                  value={task.domain}
                  onChange={e => handleFieldUpdate('domain', e.target.value)}
                  className="w-full h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white"
                >
                  <option value="wellversed.in">wellversed.in</option>
                  <option value="store.wellversed.in">store.wellversed.in</option>
                  <option value="Both">Both</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#555555] block mb-1">Deadline</label>
                <Input
                  data-testid="task-deadline-input"
                  type="date"
                  value={task.deadline ? task.deadline.split('T')[0] : ''}
                  onChange={e => handleFieldUpdate('deadline', e.target.value || null)}
                  className="h-8 text-xs border-[#E8E8E8] rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="border border-[#E8E8E8] rounded-md p-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-3">Info</h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-[#555555]">Created by</span>
                <span className="text-[#0F0F0F] font-medium">{task.created_by_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Current owner</span>
                <span className="text-[#0F0F0F] font-medium">{task.current_owner_name || 'Unassigned'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Created</span>
                <span className="text-[#0F0F0F]">{new Date(task.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#555555]">Updated</span>
                <span className="text-[#0F0F0F]">{new Date(task.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
